import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Input } from "../ui/input";
import { Tooltip } from "../ui/tooltip";
import { Plus, Edit2, Trash2, Search, Users, Building } from "lucide-react";
import { Badge } from "../ui/badge";
import { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { useColumnVisibility, getDefaultVisibility } from "../../hooks/useColumnVisibility";
import { DataTable } from "../ui/data-table";
import { PositionDialog } from "./PositionDialog";
import { toast } from "sonner";
import { usePageSize } from "../../hooks/usePageSize";
import {
  GET_POSITIONS_PAGINATED,
  GET_POSITION_STATISTICS,
  CREATE_POSITION_MUTATION,
  UPDATE_POSITION_MUTATION,
  DELETE_POSITION_MUTATION,
} from "../../graphql/queries";
import { usePermission } from "../../hooks/usePermission";

interface Position {
  id: string;
  name: string;
  description?: string;
  department?: string;
  isActive: boolean;
  employeeCount: number;
  createdAt: string;
  updatedAt: string;
}

interface PositionManagementProps {
  currentUserRole?: string;
}

export const PositionManagement: React.FC<PositionManagementProps> = ({
  currentUserRole,
}) => {
  const canManage = usePermission("positions.manage");
  const { pageSize: defaultPageSize } = usePageSize();
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(
    null
  );
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'name', desc: false }
  ]);

  // Debounced search
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination(prev => ({ ...prev, pageIndex: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when sorting changes
  React.useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [sorting]);

  // Convert TanStack sorting to GraphQL format
  const sortBy = sorting[0]?.id;
  const sortOrder = sorting[0]?.desc ? 'desc' : 'asc';

  // Fetch positions with pagination
  const { data: positionsData, loading: positionsLoading, refetch: refetchPositions } = useQuery(GET_POSITIONS_PAGINATED, {
    variables: {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy,
      sortOrder,
      searchTerm: debouncedSearchTerm || undefined,
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log("✅ Positions loaded:", data.positionsPaginated?.positions?.length);
    },
    onError: (error) => {
      console.error("❌ Error loading positions:", error);
      toast.error("Błąd podczas ładowania stanowisk");
    },
  });

  const { data: statsData } = useQuery(GET_POSITION_STATISTICS, {
    onError: (error) => {
      console.error("❌ Error loading position statistics:", error);
    },
  });

  const [createPosition] = useMutation(CREATE_POSITION_MUTATION);
  const [updatePosition] = useMutation(UPDATE_POSITION_MUTATION);
  const [deletePosition] = useMutation(DELETE_POSITION_MUTATION);

  const positions = positionsData?.positionsPaginated?.positions || [];
  const totalPositions = positionsData?.positionsPaginated?.total || 0;
  const pageCount = positionsData?.positionsPaginated?.totalPages || 0;
  const statistics = statsData?.positionStatistics || {
    totalPositions: 0,
    activePositions: 0,
    positionsWithEmployees: 0,
    emptyPositions: 0,
  };

  const handleSave = async (id: string | null, data: any) => {
    try {
      if (id) {
        await updatePosition({
          variables: { id, data },
        });
        toast.success("Stanowisko zostało zaktualizowane");
      } else {
        await createPosition({
          variables: { data },
        });
        toast.success("Stanowisko zostało dodane");
      }
      await refetchPositions();
    } catch (error: any) {
      console.error("Error saving position:", error);
      throw new Error(error.message || "Błąd podczas zapisywania stanowiska");
    }
  };

  const handleAddNew = () => {
    setSelectedPosition(null);
    setDialogOpen(true);
  };

  const handleEdit = (position: Position) => {
    setSelectedPosition(position);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPosition(null);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Czy na pewno chcesz usunąć to stanowisko?")) {
      try {
        await deletePosition({
          variables: { id },
        });
        await refetchPositions();
        toast.success("Stanowisko zostało usunięte");
      } catch (error: any) {
        console.error("Error deleting position:", error);
        toast.error(error.message || "Błąd podczas usuwania stanowiska");
      }
    }
  };

  const columns: ColumnDef<Position>[] = React.useMemo(() => [
    {
      accessorKey: "name",
      header: "Nazwa stanowiska",
      cell: ({ row }) => (
        <div className="font-medium min-w-[150px]">
          {row.original.name}
        </div>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "department",
      header: "Dział",
      cell: ({ row }) => row.original.department || "-",
      enableSorting: true,
    },
    {
      accessorKey: "description",
      header: "Opis",
      cell: ({ row }) => (
        <div className="max-w-xs truncate" title={row.original.description}>
          {row.original.description || "-"}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "employeeCount",
      header: "Pracownicy",
      cell: ({ row }) => (
        <div className="flex items-center justify-center space-x-1">
          <Users className="w-4 h-4 text-gray-400" />
          <span>{row.original.employeeCount}</span>
        </div>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "isActive",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? 'default' : 'destructive'}>
          {row.original.isActive ? 'Aktywne' : 'Nieaktywne'}
        </Badge>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "createdAt",
      header: "Data utworzenia",
      cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString("pl-PL"),
      enableSorting: false,
      meta: { defaultHidden: true },
    },
    {
      id: "actions",
      header: "Akcje",
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex space-x-2">
          {canManage && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEdit(row.original)}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              {row.original.employeeCount > 0 ? (
                <Tooltip
                  content="Nie można usunąć stanowiska z przypisanymi pracownikami"
                  side="top"
                >
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(row.original.id)}
                    disabled={true}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(row.original.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </>
          )}
        </div>
      ),
      enableSorting: false,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canManage]);

  const defaults = React.useMemo(() => getDefaultVisibility(columns), [columns]);
  const { columnVisibility, setColumnVisibility, resetToDefaults } = useColumnVisibility('positions', defaults);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-foreground">
            Zarządzanie stanowiskami
          </h2>
          <p className="text-muted-foreground mt-2">
            Dodawaj, edytuj i zarządzaj stanowiskami pracy
          </p>
        </div>
        {canManage && (
          <Button
            onClick={handleAddNew}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Dodaj stanowisko</span>
          </Button>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Wszystkie stanowiska</p>
                <p className="text-2xl font-bold">
                  {statistics.totalPositions}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Building className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Aktywne stanowiska</p>
                <p className="text-2xl font-bold">
                  {statistics.activePositions}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  Z przypisanymi pracownikami
                </p>
                <p className="text-2xl font-bold">
                  {statistics.positionsWithEmployees}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Building className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Wolne stanowiska</p>
                <p className="text-2xl font-bold">
                  {statistics.emptyPositions}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Lista stanowisk</CardTitle>
              <CardDescription>
                Zarządzaj stanowiskami pracy w firmie ({totalPositions} rekordów)
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <Search className="w-4 h-4 text-gray-400" />
              <Input
                placeholder="Szukaj stanowisk..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={positions}
            pageCount={pageCount}
            pagination={pagination}
            onPaginationChange={setPagination}
            sorting={sorting}
            onSortingChange={setSorting}
            loading={positionsLoading}
            manualPagination={true}
            manualSorting={true}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            onResetColumnVisibility={resetToDefaults}
          />
        </CardContent>
      </Card>

      <PositionDialog
        position={selectedPosition}
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSave}
      />
    </div>
  );
};
